/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Table,
  Typography,
  useTheme,
  useForm,
  Pagination,
  SearchInput,
  UtilitySelect,
  Option,
  UtilityButton,
  Checkbox,
} from '@quickbit/qb-design-system';
import { mapValues as _mapValues } from 'lodash';
import { MouseEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useDebounce, useSessionStorage } from 'react-use';
import { QBLoader } from 'components';
import { COUNTRY_OPTIONS } from 'utils';
import {
  SkinningCustomerFilters,
  SortOrder,
  searchSkinningCustomers,
} from '../api';
import { getLabel } from '../types';

const PAGE_SIZE = 16;

export const SkinningCustomerList = () => {
  const { spacing } = useTheme();
  const navigate = useNavigate();

  const [filterStore, setFilterStore] = useSessionStorage<{
    filters: SkinningCustomerFilters;
    pagination: {
      page: number;
      size: number;
    };
  } | null>('qbSkinningCustomerFilterStore');

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<SkinningCustomerFilters>(
    {
      country: filterStore?.filters.country || null,
      email: filterStore?.filters.email || null,
      name: filterStore?.filters.name || null,
      id: filterStore?.filters.id || null,
      skin: filterStore?.filters.skin || null,
      is_verified: filterStore?.filters.is_verified || null,
    },
    {}
  );

  const toggleIsVerified = (showOnlyUnapproved: boolean) => {
    setters.set({
      ...fields,
      is_verified: showOnlyUnapproved ? '1' : '0',
    });
  };

  const showOnlyUnapproved = fields.is_verified === '1' ? false : true;

  const headers = [
    { label: getLabel('uuid'), cellKey: 'id', sort: false },
    {
      label: getLabel('skin'),
      cellKey: 'skin',
      sort: false,
    },
    { label: getLabel('name'), cellKey: 'name', sort: false },
    { label: getLabel('email'), cellKey: 'email', sort: false },
    {
      label: getLabel('dob'),
      cellKey: 'dob',
      sort: false,
    },
    { label: getLabel('country'), cellKey: 'country', sort: false },
    { label: getLabel('createdAt'), cellKey: 'createdAt', sort: false },
    { label: getLabel('lastUpdated'), cellKey: 'lastUpdated', sort: false },
    { label: getLabel('isVerified'), cellKey: 'isVerified', sort: false },
  ];

  const [{ value, loading }, search] = useAsyncFn(async () => {
    const { ...stringFilters } = fields;

    const filters: SkinningCustomerFilters = _mapValues(stringFilters, (v) =>
      v?.trim()
    );

    const appliedFilters = {
      filters,
      pagination: {
        page: page,
        size: PAGE_SIZE,
      },
      sort: {
        field: 'id',
        order: SortOrder.DESC,
      },
    };
    const data = await searchSkinningCustomers(
      appliedFilters.filters,
      appliedFilters.pagination,
      appliedFilters.sort
    );
    setFilterStore(appliedFilters);
    return data;
  }, [page, fields]);

  useDebounce(
    () => {
      void search();
    },
    500,
    [search]
  );

  const rows = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!value || !value.customers) {
      setPage((state) =>
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        value?.pagination.total || 0 <= PAGE_SIZE ? 1 : state
      );
      return [];
    }

    const handleViewCustomer = (e: MouseEvent<HTMLElement>, qbId: string) => {
      e.stopPropagation();
      void navigate(`/users/app/${qbId}`);
    };

    // A workaround to reset pagination if the data rows are less than the page size
    setPage((state) => (value.pagination.total <= PAGE_SIZE ? 1 : state));

    return Object.values(value.customers).map((customer) => ({
      value: {
        id: customer.id,
        qbid: customer.qbId,
        email: customer.email,
        country: customer.country,
        name: (
          <a href="#" onClick={(e) => handleViewCustomer(e, customer.qbId)}>
            {customer.firstName} {customer.lastName}
          </a>
        ),
        dob: new Date(customer.dob).toLocaleDateString('sv-se'),
        skin: customer.skin,
        createdAt: new Date(customer.ts).toLocaleDateString('sv-se'),
        lastUpdated: new Date(customer.lastUpdateTs).toLocaleDateString(
          'sv-se'
        ),
        isVerified: customer.isVerified ? 'Verified' : 'Not Verified',
      },
    }));
  }, [value, navigate]);

  const handleRowClicked = (row: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    void navigate(`${row.id}`);
  };

  const handleResetFilters = () => {
    setPage(1);
    setters.set({
      id: null,
      country: null,
      email: null,
      name: null,
      skin: null,
      is_verified: null,
    });
  };
  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(1, auto)',
            alignItems: 'center',
            gap: spacing.xxs,
          }}
        >
          <Typography variant="subtitle">Skinning Customers</Typography>
        </div>
      </Header>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto auto auto auto',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="User ID"
          value={fields.id ?? ''}
          onChange={(e) => setters.id?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Skin"
          value={fields.skin ?? ''}
          onChange={(e) => setters.skin?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Name"
          value={fields.name ?? ''}
          onChange={(e) => setters.name?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Email"
          value={fields.email ?? ''}
          onChange={(e) => setters.email?.set(e.target.value)}
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: '8fr 1fr',
          textWrap: 'nowrap',
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xxxs,
            flexWrap: 'wrap',
          }}
        >
          <UtilitySelect
            label={
              fields.country
                ? COUNTRY_OPTIONS.find(
                    (countryOption) => countryOption.value === fields.country
                  )?.label
                : 'Country'
            }
            listTitle="Country"
            options={COUNTRY_OPTIONS}
            value={
              fields.country
                ? COUNTRY_OPTIONS.find(
                    (countryOption) => countryOption.value === fields.country
                  ) || null
                : null
            }
            onSelect={(data) => {
              setPage(1);
              setters.country?.set((data as Option).value);
            }}
          />
          <span>Show unapproved only</span>
          <Checkbox
            checked={showOnlyUnapproved}
            onChange={() => toggleIsVerified(showOnlyUnapproved)}
          />

          <UtilityButton
            variant="text"
            text="Reset filters"
            onClick={handleResetFilters}
          />
        </div>
      </div>
      {loading && <QBLoader />}
      {!loading && value && (
        <>
          <div css={{ marginTop: spacing.xxs }}>
            <Table
              headers={headers}
              rows={rows}
              onRowClick={handleRowClicked}
            />
          </div>
          <div css={{ marginTop: spacing.xxs }}>
            <Pagination
              currentPage={page}
              onChange={setPage}
              numberOfElements={value.pagination.total || 0}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}
    </>
  );
};
